import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Users,
  Calendar,
  CheckCircle,
  XCircle,
  Dice5,
  Pencil,
  Trash2,
  X,
  Check,
} from "lucide-react";
import { Header } from "../../components/layout/Header";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import { Event, EventStatus, EventType } from "../../types";
import { COLORS } from "../../config/colors";
import { formatEventDate, toDate } from "../../utils/date";

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.events
      .get(id)
      .then((res: any) => setEvent(res.data ?? null))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  function startEdit() {
    setEditName(event?.name ?? "");
    // Format the round date as YYYY-MM-DD for the date input
    const rawDate = event?.rounds[0]?.date;
    if (rawDate) {
      const d = toDate(rawDate);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      setEditDate(`${yyyy}-${mm}-${dd}`);
    } else {
      setEditDate("");
    }
    setEditNotes(event?.notes ?? "");
    setEditError("");
    setEditing(true);
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !event) return;
    setEditError("");
    setEditLoading(true);
    try {
      await api.events.update(id, {
        name: editName.trim() || undefined,
        notes: editNotes.trim() || undefined,
        roundDate: editDate || undefined,
      });
      // Update local state
      const updatedRounds = event.rounds.map((r, i) =>
        i === 0 && editDate ? { ...r, date: editDate } : r,
      );
      setEvent({
        ...event,
        name: editName.trim() || event.name,
        notes: editNotes.trim(),
        rounds: updatedRounds,
      });
      setEditing(false);
    } catch (err: any) {
      setEditError(err.message ?? "Failed to save");
    } finally {
      setEditLoading(false);
    }
  }

  async function handleDelete() {
    if (!id) return;
    setDeleteLoading(true);
    try {
      await api.events.delete(id);
      navigate("/events", { replace: true });
    } catch (err: any) {
      alert(err.message ?? "Failed to delete event");
      setDeleteLoading(false);
      setConfirmDelete(false);
    }
  }

  const handleRespond = async (accept: boolean) => {
    if (!id) return;
    setResponding(true);
    try {
      await api.events.respond(id, accept);
      const res = (await api.events.get(id)) as any;
      setEvent(res.data ?? null);
    } finally {
      setResponding(false);
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (!event)
    return (
      <>
        <Header title='Event' showBack />
        <div className='px-4 py-12 text-center text-gray-500'>
          Event not found.
        </div>
      </>
    );

  const isOwner = event.createdBy === user?.uid;
  const isPlayer = event.playerIds.includes(user?.uid ?? "");
  const isPari = event.eventType === EventType.PARIMUTUEL_BETTING;

  return (
    <>
      <Header title={event.name} showBack />

      <div className='px-4 py-4 space-y-4'>
        {/* ── Event info card (with inline edit) ── */}
        <Card accent={isPari ? COLORS.parimutuel : COLORS.events}>
          {editing ? (
            <form onSubmit={handleSaveEdit} className='space-y-3'>
              <div>
                <label className='block text-xs font-semibold text-gray-500 mb-1'>
                  Event Name
                </label>
                <input
                  className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400'
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder='Event name'
                  required
                />
              </div>
              <div>
                <label className='block text-xs font-semibold text-gray-500 mb-1'>
                  Date
                </label>
                <input
                  type='date'
                  className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400'
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                />
              </div>
              <div>
                <label className='block text-xs font-semibold text-gray-500 mb-1'>
                  Notes (optional)
                </label>
                <textarea
                  className='w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-400 resize-none'
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder='Add a note…'
                  rows={2}
                />
              </div>
              {editError && <p className='text-xs text-red-500'>{editError}</p>}
              <div className='flex gap-2'>
                <Button
                  type='submit'
                  size='sm'
                  loading={editLoading}
                  leftIcon={<Check size={14} />}
                >
                  Save
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => setEditing(false)}
                  leftIcon={<X size={14} />}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div className='flex items-start justify-between mb-2'>
                <div>
                  <p className='font-bold text-primary-800 text-lg'>
                    {event.name}
                  </p>
                  <p className='text-sm text-gray-500'>
                    {event.eventType.replace(/-/g, " ")}
                  </p>
                </div>
                <div className='flex items-center gap-1.5'>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      event.status === EventStatus.IN_PROGRESS
                        ? "bg-green-100 text-green-700"
                        : event.status === EventStatus.COMPLETED
                          ? "bg-gray-100 text-gray-500"
                          : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {event.status}
                  </span>
                  {isOwner && (
                    <button
                      onClick={startEdit}
                      className='p-1.5 rounded-lg text-gray-400 hover:text-secondary-600 hover:bg-secondary-50 transition-colors'
                      title='Edit event'
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              </div>

              {event.rounds[0] && (
                <div className='text-sm text-gray-600 space-y-1 mt-2'>
                  {event.rounds[0].courseName && (
                    <div className='flex items-center gap-2'>
                      <Calendar size={14} className='text-secondary-400' />
                      {event.rounds[0].courseName}
                    </div>
                  )}
                  {event.rounds[0].date && (
                    <div className='flex items-center gap-2'>
                      <Calendar size={14} className='text-secondary-400' />
                      {formatEventDate(event.rounds[0].date)}
                    </div>
                  )}
                  <div className='flex items-center gap-2'>
                    <Users size={14} className='text-secondary-400' />
                    {event.playerIds.length} player
                    {event.playerIds.length !== 1 ? "s" : ""}
                  </div>
                </div>
              )}

              {event.notes && (
                <p className='text-sm text-gray-500 mt-2 italic'>
                  "{event.notes}"
                </p>
              )}
            </>
          )}
        </Card>

        {/* Parimutuel shortcut */}
        {isPari && (
          <Button
            fullWidth
            leftIcon={<Dice5 size={18} />}
            onClick={() => navigate(`/events/${event.id}/parimutuel`)}
          >
            Open Parimutuel Board
          </Button>
        )}

        {/* Invitation response */}
        {!isPlayer && (
          <Card>
            <p className='text-sm font-semibold text-primary-800 mb-3'>
              You've been invited
            </p>
            <div className='flex gap-2'>
              <Button
                fullWidth
                variant='primary'
                loading={responding}
                leftIcon={<CheckCircle size={16} />}
                onClick={() => handleRespond(true)}
              >
                Accept
              </Button>
              <Button
                fullWidth
                variant='outline'
                loading={responding}
                leftIcon={<XCircle size={16} />}
                onClick={() => handleRespond(false)}
              >
                Decline
              </Button>
            </div>
          </Card>
        )}

        {/* Delete event */}
        {isOwner &&
          (confirmDelete ? (
            <Card>
              <p className='text-sm font-semibold text-red-700 mb-1'>
                Delete this event?
              </p>
              <p className='text-xs text-gray-500 mb-3'>
                This will permanently delete the event, all invitations, and the
                parimutuel board (if any). This cannot be undone.
              </p>
              <div className='flex gap-2'>
                <Button
                  fullWidth
                  loading={deleteLoading}
                  onClick={handleDelete}
                  leftIcon={<Trash2 size={14} />}
                  style={{ backgroundColor: "#dc2626" }}
                >
                  Yes, Delete
                </Button>
                <Button
                  fullWidth
                  variant='outline'
                  onClick={() => setConfirmDelete(false)}
                  leftIcon={<X size={14} />}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className='w-full flex items-center justify-center gap-2 py-2 text-sm text-red-400 hover:text-red-600 transition-colors'
            >
              <Trash2 size={14} />
              Delete Event
            </button>
          ))}
      </div>
    </>
  );
}
